import React from "react";
import { Award, Shield, DollarSign, ExternalLink } from "lucide-react";
import { Price } from "@/context/CurrencyContext";

/**
 * Price comparison widget shown on tour detail page.
 * Highlights "best price guaranteed" vs OTAs.
 * If the tour document has ota_prices populated (set in admin Tour Editor), shows them as comparison.
 */
export default function PriceComparison({ tour }) {
  const directPrice = tour.price_usd || Math.round((tour.price_from || 0) / 83);
  const otaPrices = tour.ota_prices || {};
  const OTA_LABELS = {
    getyourguide: "GetYourGuide",
    viator: "Viator",
    klook: "Klook",
    tourradar: "TourRadar",
    expedia: "Expedia",
    civitatis: "Civitatis",
  };

  const entries = Object.entries(otaPrices)
    .filter(([_, p]) => p && Number(p) > 0)
    .map(([k, p]) => ({ key: k, name: OTA_LABELS[k] || k, price: Number(p) }))
    .sort((a, b) => b.price - a.price);

  const maxOtaPrice = entries.length > 0 ? Math.max(...entries.map((e) => e.price)) : 0;
  const savings = maxOtaPrice > directPrice ? maxOtaPrice - directPrice : 0;

  return (
    <div className="bg-gradient-to-br from-saffron/5 via-marigold/10 to-cream-100 border border-saffron/30 rounded-sm p-6" data-testid="price-comparison">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-saffron text-white flex items-center justify-center">
          <Award className="w-5 h-5" />
        </div>
        <div>
          <div className="font-serif text-lg text-deepred">Best price guaranteed</div>
          <div className="text-xs text-foreground/65 uppercase tracking-wider">Book direct &amp; save</div>
        </div>
      </div>

      <div className="space-y-2 mb-5">
        <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-300 rounded-sm" data-testid="price-direct">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-700" />
            <span className="text-sm font-medium text-foreground">Direct on ttiholidays.com</span>
          </div>
          <div className="font-serif text-xl text-emerald-700">
            <Price usd={directPrice} />
          </div>
        </div>

        {entries.length > 0 ? (
          entries.map((e) => {
            const diff = e.price - directPrice;
            return (
              <div
                key={e.key}
                className="flex items-center justify-between p-3 bg-white/60 border border-cream-200 rounded-sm"
                data-testid={`price-${e.key}`}
              >
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-3.5 h-3.5 text-foreground/40" />
                  <span className="text-sm text-foreground/75">Same tour on {e.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-serif text-foreground/60 line-through">
                    <Price usd={e.price} />
                  </div>
                  {diff > 0 && (
                    <div className="text-[10px] uppercase tracking-wider text-deepred font-semibold">
                      Save <Price usd={diff} /> here
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-xs text-foreground/55 italic text-center py-2">
            Same tour also available on GetYourGuide, Viator and Klook — but always cheapest here.
          </div>
        )}
      </div>

      <div className="bg-white/70 rounded-sm p-3 text-xs text-foreground/75 leading-relaxed flex items-start gap-2">
        <DollarSign className="w-3.5 h-3.5 text-saffron mt-0.5 flex-shrink-0" />
        <span>
          {savings > 0 ? (
            <>
              Book here and save up to <strong className="text-deepred"><Price usd={savings} /></strong> per traveler. OTAs charge an
              extra 20–30% — by booking direct, that saving stays with <em>you</em>.
            </>
          ) : (
            <>
              By booking direct you skip the OTA commission (typically 20–30%) — your money goes into a better tour,
              not into a marketplace&rsquo;s pockets.
            </>
          )}
        </span>
      </div>
    </div>
  );
}
